import React from 'react';
import { motion } from 'motion/react';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';

interface AddOnCardProps {
  /** Image ID of the add-on */
  imageId?: string | null;
  /** Name of the add-on */
  name: string;
  /** Total price to display (in cents/lowest unit) */
  price: number;
  /** Quantity to display */
  quantity: number;
  /** Currency code (e.g., 'GBP', 'USD') - required for proper formatting */
  currency?: string;
  /** Click handler */
  onClick?: () => void;
  /** Optional width class (default: w-48) */
  widthClass?: string;
  /** Optional status badge (e.g., "Unavailable") */
  statusBadge?: string;
  /** Optional disabled state */
  disabled?: boolean;
  /** Optional additional classes */
  className?: string;
}

/**
 * Reusable add-on display card with image, name, price, and quantity.
 * Used in Cart pages and Transfer pages for displaying add-ons.
 */
export const AddOnCard: React.FC<AddOnCardProps> = ({
  imageId,
  name,
  price,
  quantity,
  currency,
  onClick,
  widthClass = 'w-48',
  statusBadge,
  disabled,
  className = '',
}) => {
  // Check if image is valid
  const hasValidImage = imageId && imageId !== '00000000-0000-0000-0000-000000000000';
  const imageUrl = hasValidImage ? getSignedMediaUrl(imageId, 'image').url : null;

  // Format price using currency if provided, otherwise show raw amount
  const formattedPrice = currency 
    ? formatCurrencyForDisplay(price, currency)
    : `${(price / 100).toFixed(2)}`;

  return (
    <motion.div
      whileHover={disabled ? undefined : { scale: 1.02 }}
      whileTap={disabled ? undefined : { scale: 0.98 }}
      onClick={disabled ? undefined : onClick}
      className={`relative aspect-[4/3] ${widthClass} flex-shrink-0 rounded-lg overflow-hidden ${
        disabled ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:ring-2 hover:ring-blue-500/50'
      } ${className}`}
      style={{
        background: imageUrl
          ? `url(${imageUrl})`
          : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

      {/* Quantity Badge - Top Right */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-black/60 rounded-full px-1.5 py-0.5">
        <span className="text-white text-xs font-medium">×{quantity}</span>
      </div>

      {/* Status Badge - Center (if unavailable) */}
      {statusBadge && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-gray-400 text-sm font-medium bg-black/60 px-2 py-1 rounded">
            {statusBadge}
          </span>
        </div>
      )}

      {/* Name and Price - Bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-2.5">
        <h5 className="text-white font-semibold text-sm mb-0.5 line-clamp-1">{name}</h5>
        <p className="text-blue-400 font-bold text-sm">
          {formattedPrice}
        </p>
      </div>
    </motion.div>
  );
};

export default AddOnCard;
